<script setup>
import {reactive, computed, ref, onMounted, watch, nextTick} from 'vue'
import moment from 'moment'
import api from '@/services/api'
import {useToastsStore} from '@/stores/toasts'
import {useUserStore} from '@/stores/user'
import {useModalsStore} from '@/stores/modals'
import {useCategoriesStore} from '@/stores/categories'
import {useItemDataStore} from '@/stores/itemData'
import {useFormStore} from '@/stores/form'
import {useRouter} from "vue-router"

const props = defineProps({
    currentData: Object,
    field: Object,
    formId: String,
})

const router = useRouter()

const toastsStore = useToastsStore()
const userStore = useUserStore()
const modalsStore = useModalsStore()
const categoriesStore = useCategoriesStore()
const itemDataStore = useItemDataStore()
const formStore = useFormStore(props.formId)

const state = reactive({
    bookings: [],
    waitingListBookings: [],
    selectedBookingsIds: [],
    selectedWaitingListIds: [],
    voucherCodeProviders: {},
    parentsEmailSending: false,
    confirmationEmailSending: false,
    parents: {},
    waitingListParents: {},
    childNames: {},
    waitingListChildNames: {},
})

const newCourseDatesEmailLastSent = computed(() => props.currentData.newCourseDatesEmailLastSent)
const allCheckboxesChecked = computed(() => state.bookings.length > 0 && state.bookings.length === state.selectedBookingsIds.length)
const allWaitingListCheckboxesChecked = computed(() => state.waitingListBookings.length > 0 && state.waitingListBookings.length === state.selectedWaitingListIds.length)
const hasArchiveAccess = computed(() => userStore.access('odp_course_bookings', 'archive'))

const courseType = ref(null)

watch(
    () => props.currentData.courseId,
    async (newCourseId) => {
        if (!newCourseId) {
            courseType.value = null
            return
        }
        const res = await itemDataStore.get({
            tableName: 'odp_courses',
            id: newCourseId
        })
        courseType.value = res?.courseType || null
    },
    {immediate: true}
)

onMounted(() => {
    props.field.label = null
    setTimeout(() => {
        if (!props.currentData.id) {
            props.field.hidden = true
            return
        }
        props.field.replaceFormgroup = true
        setBookings()
        setWaitingListBookings()

        formStore.setFieldProperty({
            fieldName: 'newCourseDatesEmailLastSent',
            property: 'hidden',
            value: true
        })
    }, 500)
})

const setBookings = async () => {
    const vProviders = (await itemDataStore.getAll({tableName: 'voucher_provider'}))
        .reduce((acc, o) => {
            acc[o.id] = o.title
            return acc
        }, {})

    state.voucherCodeProviders = (await itemDataStore.getAll({tableName: 'franchise_voucher_code'}))
        .reduce((acc, o) => {
            acc[o.id] = vProviders[o.voucherProviderId]
            return acc
        }, {})

    const {data: {values: ids}} = await api.get('api/component/odp_course_bookings/id', {
        params: {courseDateId: props.currentData.id, hasExpired: 0, archived: 0, isArchived: 0, sort: 'id'}
    })

    const bookings = await itemDataStore.preload({tableName: 'odp_course_bookings', ids})
    if (bookings) {
        state.bookings = bookings
        const childIds = bookings.map(o => o.childId)
        await setChildNames(childIds)
        await setParentData(childIds)
    }
}

const setWaitingListBookings = async () => {
    const {data: {values: ids}} = await api.get('api/component/odp_waiting_list/id', {
        params: {courseDateId: props.currentData.id, isArchived: 0, sort: 'id'}
    })
    state.waitingListBookings = await itemDataStore.preload({tableName: 'odp_waiting_list', ids})
    if (state.waitingListBookings) {
        const childIds = state.waitingListBookings.map(o => o.childId)
        await setChildNames(childIds, true)
        await setParentData(childIds, true)
    }
}

const sendEmail = (params) => api.post('api/email/send', params)

const sendEmailMulti = async (notificationTitle, emails) => {
    const {data: {id: notificationId}} = await api.post('api/notification', {
        title: 'Bulk email',
        data: {title: notificationTitle, message: 'Starting...', percentage: 0}
    })
    toastsStore.add({title: 'Bulk email', disableAutoHide: true, percentage: 0, notificationId})
    return api.post('api/email/send-multi', {notificationId, emails})
}

const sendPaymentReminder = async (e, bookingId) => {
    if (e?.target) e.target.disabled = true
    const parent = await getBookingsParent(bookingId)
    const franchise = await getBookingsFranchise(bookingId)

    await itemDataStore.update({
        tableName: 'odp_course_bookings',
        id: bookingId,
        data: {amountPaid: 0.00, paymentReminderLastSent: Math.ceil(Date.now() / 1000)}
    })

    let params = {
        subject: 'Waiting list - a space has come available!',
        templateId: 17,
        templateVars: {bookingId},
        to: parent.email,
        replyTo: franchise.email
    }
    await sendEmail(params)
    params.to = 'info@theoutdoorsproject.co.uk'
    delete params.replyTo
    await sendEmail(params)
    await setBookings()
}

const sendVoucherPaymentReminder = async (e, bookingId) => {
    if (e?.target) e.target.disabled = true
    await itemDataStore.update({
        tableName: 'odp_course_bookings',
        id: bookingId,
        data: {voucherPaymentReminderLastSent: Math.ceil(Date.now() / 1000)}
    })
    const parent = await getBookingsParent(bookingId)
    const franchise = await getBookingsFranchise(bookingId)
    const params = {
        subject: 'Voucher payment reminder - ACTION REQUIRED',
        templateId: 128,
        templateVars: {bookingId},
        to: parent.email,
        replyTo: franchise.email
    }
    await sendEmail(params)
    params.to = 'info@theoutdoorsproject.co.uk'
    delete params.replyTo
    await sendEmail(params)
    await setBookings()
}

const sendWaitingListBookingReminder = async (bookingId) => {
    await itemDataStore.update({
        tableName: 'odp_waiting_list',
        id: bookingId,
        data: {bookingNotificationLastSent: Math.ceil(Date.now() / 1000)}
    })
    const parent = await getBookingsParent(bookingId, true)
    const franchise = await getBookingsFranchise(bookingId, true)
    const params = {
        templateId: 19,
        templateVars: {waitingListId: bookingId},
        to: parent.email,
        subject: `${parent.firstName}, New Course Dates Available`,
        replyTo: franchise.email
    }
    await sendEmail(params)
    params.to = 'info@theoutdoorsproject.co.uk'
    delete params.replyTo
    await sendEmail(params)
    await setWaitingListBookings()
}

const sendConfirmationEmail = async () => {
    state.confirmationEmailSending = true
    const franchise = await getBookingsFranchise(state.selectedBookingsIds[0])
    const bookings = await itemDataStore.preload({
        tableName: 'odp_course_bookings',
        ids: state.selectedBookingsIds
    })
    const fVoucherCodeIds = [...new Set(bookings.map(o => o.franchiseVoucherCodeId))]
    const fVoucherCodes = await itemDataStore.preload({
        tableName: 'franchise_voucher_code',
        ids: fVoucherCodeIds
    })
    const vProviderIds = [...new Set(fVoucherCodes.map(o => o.voucherProviderId))]
    const vProviders = await itemDataStore.preload({tableName: 'voucher_provider', ids: vProviderIds})

    const courseDate = await itemDataStore.get({
        tableName: 'odp_course_dates',
        id: bookings[0].courseDateId
    })
    const school = await itemDataStore.get({tableName: 'odp_schools', id: courseDate.schoolId})
    const course = await itemDataStore.get({tableName: 'odp_courses', id: courseDate.courseId})
    const childIds = bookings.map(o => o.childId)
    const children = await itemDataStore.preload({tableName: 'odp_children', ids: childIds})
    const cats = await categoriesStore.getTableColumnData({
        tableName: 'm_registrants_registrants',
        columnName: 'children'
    })
    const parentIds = cats.filter(o => childIds.indexOf(o.catId) > -1).map(o => o.itemId)
    const parents = await itemDataStore.preload({tableName: 'm_registrants_registrants', ids: parentIds})

    const emailItems = []
    for (const booking of bookings) {
        const child = children.find(o => o.id === booking.childId)
        const parentId = cats.find(o => o.catId === booking.childId)?.itemId
        const parent = parents.find(o => o.id === parentId)
        const vCode = fVoucherCodes.find(o => o.id === booking.franchiseVoucherCodeId)
        const vProv = vProviders.find(o => o.id === vCode?.voucherProviderId)

        const vars = {
            bookingId: booking.id,
            booking,
            child,
            customer: parent,
            voucherProvider: vProv,
            franchiseVoucherCode: vCode,
            course,
            courseDate,
            school
        }
        emailItems.push({
            to: parent.email,
            subject: `Booking confirmation #${booking.id} - ${child.firstName} ${child.lastName}`,
            templateId: 18,
            templateVars: vars,
            replyTo: franchise.email
        })
        emailItems.push({
            to: franchise.email === 'info@theoutdoorsproject.co.uk' ? franchise.email : `${franchise.email} info@theoutdoorsproject.co.uk`,
            subject: `Booking confirmation #${booking.id} - ${child.firstName} ${child.lastName}`,
            templateId: 18,
            templateVars: vars
        })
    }
    await sendEmailMulti('Confirmation emails', emailItems)
    state.confirmationEmailSending = false
}

const sendNewCourseDatesEmail = async () => {
    const subject = 'Next Term After School Club - Now Booking'
    const date = Math.ceil(Date.now() / 1000)

    for (const id of state.selectedBookingsIds) {
        const parent = await getBookingsParent(id)
        if (parent) await sendEmail({to: parent.email, subject, templateId: 20, templateVars: {bookingId: id}})
    }
    for (const id of state.selectedWaitingListIds) {
        const parent = await getBookingsParent(id, true)
        if (parent) await sendEmail({to: parent.email, subject, templateId: 20, templateVars: {bookingId: id}})
    }

    props.currentData.newCourseDatesEmailLastSent = date
    await itemDataStore.update({
        tableName: 'odp_course_dates',
        id: props.currentData.id,
        data: {newCourseDatesEmailLastSent: date}
    })
}

const addToBookings = async (e, waitingListId) => {
    if (e?.target) e.target.disabled = true

    try {
        const waitListItem = await itemDataStore.get({tableName: 'odp_waiting_list', id: waitingListId})
        const courseDate = await itemDataStore.get({
            tableName: 'odp_course_dates',
            id: waitListItem.courseDateId
        })
        const now = Math.ceil(Date.now() / 1000)
        const agreedPrice = now >= courseDate.startDate ? courseDate.price : waitListItem.agreedPrice

        await api.post('api/component/odp_course_bookings', {
            childId: waitListItem.childId,
            courseDateId: waitListItem.courseDateId,
            amountPaid: 0.00,
            paymentReminderLastSent: 0,
            hasExpired: 0,
            archived: 0,
            franchiseVoucherCodeId: waitListItem.franchiseVoucherCodeId,
            agreedPrice,
            permissionToWalkHome: waitListItem.permissionToWalkHome
        })

        await itemDataStore.update({
            tableName: 'odp_waiting_list',
            id: waitingListId,
            data: {isArchived: 1}
        })
        await setBookings()
        await setWaitingListBookings()
    } catch (err) {
        // Handle error if needed
    }
}

const addMultipleToBookings = (e) => {
    if (confirm('Are you sure?')) {
        state.selectedWaitingListIds.forEach(id => addToBookings(e, id))
        state.selectedWaitingListIds = []
    }
}

const archiveWaitListItems = () => {
    if (confirm('Are you sure?')) {
        state.selectedWaitingListIds.forEach(id => {
            state.waitingListBookings = state.waitingListBookings.filter(o => o.id !== id)
            itemDataStore.update({tableName: 'odp_waiting_list', id, data: {isArchived: 1}})
        })
        state.selectedWaitingListIds = []
    }
}

const sendWaitListReminders = () => {
    if (confirm('Are you sure?')) {
        state.selectedWaitingListIds.forEach(id => sendWaitingListBookingReminder(id))
        state.selectedWaitingListIds = []
    }
}

const removeBookingFromUi = (bookingId) => {
    state.bookings = state.bookings.filter(o => o.id !== bookingId)
    state.selectedBookingsIds = state.selectedBookingsIds.filter(id => id !== bookingId)
}

const archiveBooking = async (bookingId) => {
    removeBookingFromUi(bookingId)
    await itemDataStore.update({tableName: 'odp_course_bookings', id: bookingId, data: {archived: 1}})
}

const cancelBooking = async (bookingId) => {
    removeBookingFromUi(bookingId)
    try {
        await api.post('/api/controller/odp/cancel-booking', {bookingId})
    } catch (err) {
        // Handle error if needed
    }
}

const formatCurrency = (val) => val ? new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
}).format(val) : '£0.00'
const getDate = (ts) => moment(ts).format('DD MMM YYYY, HH:mm')
const isNotPaid = (val) => val === null || parseFloat(val) === 0
const isChecked = (id, wait) => (wait ? state.selectedWaitingListIds : state.selectedBookingsIds).includes(id)
const isButtonDisabled = (wait) => (wait ? state.selectedWaitingListIds : state.selectedBookingsIds).length === 0

const toggleCheckbox = (id, wait) => {
    const arr = wait ? state.selectedWaitingListIds : state.selectedBookingsIds
    const i = arr.indexOf(id)
    i === -1 ? arr.push(id) : arr.splice(i, 1)
}

const selectAllCheckboxes = (wait) => {
    const target = wait ? 'selectedWaitingListIds' : 'selectedBookingsIds'
    const source = wait ? state.waitingListBookings : state.bookings
    state[target] = state[target].length < source.length ? source.map(o => o.id) : []
}

const getBookingsParent = async (id, wait) => {
    const table = wait ? 'odp_waiting_list' : 'odp_course_bookings'
    const {childId} = await itemDataStore.get({tableName: table, id})
    const cats = await categoriesStore.getTableColumnData({
        tableName: 'm_registrants_registrants',
        columnName: 'children'
    })
    const parentId = cats.find(o => o.catId === childId)?.itemId
    return parentId ? itemDataStore.get({tableName: 'm_registrants_registrants', id: parentId}) : null
}

const getBookingsFranchise = async (id, wait) => {
    const table = wait ? 'odp_waiting_list' : 'odp_course_bookings'
    const {courseDateId} = await itemDataStore.get({tableName: table, id})
    const {courseId} = await itemDataStore.get({tableName: 'odp_course_dates', id: courseDateId})
    const {franchiseId} = await itemDataStore.get({tableName: 'odp_courses', id: courseId})
    return itemDataStore.get({tableName: 'odp_franchises', id: franchiseId})
}

const setChildNames = async (ids, wait) => {
    if (!ids.length) return
    const items = await itemDataStore.preload({tableName: 'odp_children', ids})
    const map = items.reduce((acc, o) => {
        acc[o.id] = `${o.firstName} ${o.lastName}`
        return acc
    }, {})
    wait ? state.waitingListChildNames = map : state.childNames = map
}

const setParentData = async (ids, wait) => {
    if (!ids.length) return

    const cats = await categoriesStore.getTableColumnData({
        tableName: 'm_registrants_registrants',
        columnName: 'children'
    })
    const parentIds = []
    const childToParent = {}
    ids.forEach(childId => {
        const parentId = cats.find(o => o.catId === childId)?.itemId
        if (parentId) {
            parentIds.push(parentId)
            childToParent[childId] = parseInt(parentId)
        }
    })

    const objs = await itemDataStore.preload({tableName: 'm_registrants_registrants', ids: parentIds})
    const data = {}
    Object.entries(childToParent).forEach(([childId, parentId]) => {
        data[childId] = objs.find(o => o.id === parentId)
    })
    wait ? state.waitingListParents = data : state.parents = data
}

const openWorksheet = async (templateId, waitingList, documentTitle) => {
    const {data: html} = await api.get('api/controller/document/' + templateId, {
        params: {
            courseDateId: props.currentData.id,
            bookingIds: waitingList ? [] : state.selectedBookingsIds,
            waitListIds: waitingList ? state.selectedWaitingListIds : []
        }
    })
    const win = window.open()
    if (win) {
        win.document.documentElement.innerHTML = html
        win.document.title = documentTitle
    }
}

const sendParentsEmail = async (waitingList) => {
    state.parentsEmailSending = true
    const bookingIds = waitingList ? state.selectedWaitingListIds : state.selectedBookingsIds
    const tableName = waitingList ? 'odp_waiting_list' : 'odp_course_bookings'

    try {
        const {data: {id: notificationId}} = await api.post('api/notification', {
            title: 'Email parents',
            data: {message: 'Starting...', percentage: 0}
        })

        await toastsStore.add({
            title: 'Email parents',
            body: 'Starting...',
            notificationId,
            createdBy: userStore.user?.id
        })

        await api.post('api/odp/send-parents-emails', {
            notificationId,
            bookingIds,
            tableName
        })
    } catch (err) {
        // Handle error if needed
    } finally {
        state.parentsEmailSending = false
    }
}

const goToBooking = (id, wait) => window.location = `/#/${wait ? 'odp_waiting_list' : 'odp_course_bookings'}/${id}`
const goToChild = (id) => router.push({name: 'form', params: {component: 'odp_children', id}})
const goToParent = (id) => router.push({name: 'form', params: {component: 'm_registrants_registrants', id}})
const openPaymentLogModal = (bookingId) => modalsStore.show({
    componentName: 'OdpPaymentLogModal',
    obj: {bookingId}
})
const showNotes = (row) => modalsStore.show({componentName: 'OdpPaymentNoteModal', obj: {row}})

watch(() => state.bookings, () => {
    nextTick(() => {
        if (typeof $ !== 'undefined') $('[data-bs-toggle="tooltip"]').tooltip()
    })
})
</script>

<template>
    <div class="mt-3 pt-3 mb-3 pb-3">
        <p v-if="!state.bookings.length">
            <em>empty</em>
        </p>

        <p v-if="state.bookings.length">
            <button v-if="courseType === 'After school club' || courseType === 'Forest school'"
                    :disabled="isButtonDisabled(false)" class="mb-2 btn btn-primary" type="button"
                    @click="openWorksheet(219, false, 'Register')">
                Register
            </button>

            <button
                v-if="['Event', 'Mac\'s Farm event', 'Saplings', 'Other event'].includes(courseType)"
                :disabled="isButtonDisabled(false)" class="mb-2 btn btn-primary" type="button"
                @click="openWorksheet(79, false, 'Holiday Clubs registration form')">
                Holiday Clubs registration form
            </button>

            <button :class="{ 'btn-warning': state.parentsEmailSending }"
                    :disabled="isButtonDisabled(false) || state.parentsEmailSending" class="mb-2 btn btn-primary"
                    type="button" @click="sendParentsEmail(false)">
                Email parents
            </button>

            <button :class="{ 'btn-warning': state.confirmationEmailSending }"
                    :disabled="isButtonDisabled(false) || state.confirmationEmailSending" class="mb-2 btn btn-primary"
                    type="button" @click="sendConfirmationEmail">
                Confirmation email
            </button>

            <button :disabled="isButtonDisabled(false)" class="mb-2 btn btn-primary" type="button"
                    @click="openWorksheet(136, false, 'Booking table')">
                Open booking table
            </button>
        </p>

        <table v-if="state.bookings.length" class="table table-sm small">
            <thead>
            <tr>
                <td><input :checked="allCheckboxesChecked" type="checkbox" @click="selectAllCheckboxes()"></td>
                <td/>
                <td>Child</td>
                <td>Parent</td>
                <td>Agreed price</td>
                <td>Amount paid</td>
                <td>Credit amount</td>
                <td>Payment reminder (last sent)</td>
                <td>Voucher provider</td>
                <td>Voucher payment</td>
                <td>Booking note</td>
                <td v-if="hasArchiveAccess"/>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(booking, index) in state.bookings" :key="booking.id">
                <td><input :checked="isChecked(booking.id)" type="checkbox" @click="toggleCheckbox(booking.id)"></td>
                <td><a href @click.prevent="goToBooking(booking.id)">{{ index + 1 }}</a></td>
                <td>
                    <span v-if="state.childNames[booking.childId]">
                        <a href @click.prevent="goToChild(booking.childId)">{{ state.childNames[booking.childId] }}</a>
                    </span>
                </td>
                <td>
                    <span v-if="state.parents[booking.childId]">
                        <a href @click.prevent="goToParent(state.parents[booking.childId].id)">
                            {{ state.parents[booking.childId].firstName }} {{ state.parents[booking.childId].lastName }}
                        </a>
                        <br>{{ state.parents[booking.childId].email }}
                        <br>{{ state.parents[booking.childId].telephone }}
                    </span>
                </td>
                <td>{{ formatCurrency(booking.agreedPrice) }}</td>
                <td>
                    <a href @click.prevent="openPaymentLogModal(booking.id)">
                        {{ formatCurrency(booking.amountPaid) }}
                    </a>
                </td>
                <td>{{ formatCurrency(booking.creditAmountUsed) }}</td>
                <td>
                    <span v-if="booking.placeConfirmed">Confirmed</span>
                    <span v-else-if="isNotPaid(booking.amountPaid) && !booking.franchiseVoucherCodeId">
                        <button class="btn btn-light btn-sm" type="button"
                                @click="sendPaymentReminder($event, booking.id)">Send</button>
                        <small v-if="booking.paymentReminderLastSent"
                               class="text-nowrap">{{ getDate(booking.paymentReminderLastSent * 1000) }}</small>
                    </span>
                </td>
                <td>
                    <span ref="tooltipRefs" :style="{ color: booking.voucherPaymentConfirmed ? '#5cb85c' : '#ec971f' }"
                          :title="booking.voucherPaymentConfirmed ? 'Voucher payment confirmed' : 'Voucher payment NOT confirmed'"
                          data-bs-toggle="tooltip">{{
                            state.voucherCodeProviders[booking.franchiseVoucherCodeId]
                        }}</span>
                </td>
                <td>
                    <span v-if="booking.voucherPaymentConfirmed">Confirmed</span>
                    <span v-else-if="booking.franchiseVoucherCodeId">
                        <button class="btn btn-light btn-sm" type="button"
                                @click="sendVoucherPaymentReminder($event, booking.id)">Chase voucher</button>
                        <small v-if="booking.voucherPaymentReminderLastSent"
                               class="text-nowrap">{{ getDate(booking.voucherPaymentReminderLastSent * 1000) }}</small>
                    </span>
                </td>
                <td>
                    <a href @click.prevent="showNotes(booking)">
                        <i v-if="!booking.paymentNote" class="bi-pencil-square"/>
                        {{ booking.paymentNote }}
                    </a>
                </td>
                <td v-if="hasArchiveAccess" class="text-end">
                    <div class="dropdown">
                        <button class="btn btn-light btn-sm" data-bs-toggle="dropdown">Action</button>
                        <div class="dropdown-menu dropdown-menu-end">
                            <a class="dropdown-item" @click="archiveBooking(booking.id)">Archive</a>
                            <a class="dropdown-item" @click="cancelBooking(booking.id)">Cancel and credit</a>
                        </div>
                    </div>
                </td>
            </tr>
            </tbody>
        </table>

        <div class="my-3 py-3">
            <h2>Waiting list</h2>
            <p v-if="state.waitingListBookings.length">
                <button v-if="hasArchiveAccess" :disabled="isButtonDisabled(true)" class="mb-2 btn btn-primary"
                        @click="archiveWaitListItems">Archive
                </button>
                <button :disabled="isButtonDisabled(true)" class="mb-2 btn btn-primary"
                        @click="addMultipleToBookings($event)">Add to bookings
                </button>
                <button :disabled="isButtonDisabled(true)" class="mb-2 btn btn-primary" @click="sendWaitListReminders">
                    Send reminder
                </button>
                <button :class="{ 'btn-warning': state.parentsEmailSending }"
                        :disabled="isButtonDisabled(true) || state.parentsEmailSending" class="mb-2 btn btn-primary"
                        @click="sendParentsEmail(true)">Email parents
                </button>
                <button :disabled="isButtonDisabled(true)" class="mb-2 btn btn-primary"
                        @click="openWorksheet(136, true, 'Booking table')">Open booking table
                </button>
            </p>
            <p v-if="!state.waitingListBookings.length"><em>empty</em></p>

            <table v-if="state.waitingListBookings.length" class="table table-condensed">
                <thead>
                <tr>
                    <td><input :checked="allWaitingListCheckboxesChecked" type="checkbox"
                               @click="selectAllCheckboxes(true)"></td>
                    <td/>
                    <td>Child</td>
                    <td>Parent</td>
                    <td>Agreed price</td>
                    <td>Booking reminder</td>
                    <td>Voucher provider</td>
                    <td/>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(booking, index) in state.waitingListBookings" :key="booking.id">
                    <td><input :checked="isChecked(booking.id, true)" type="checkbox"
                               @click="toggleCheckbox(booking.id, true)"></td>
                    <td><a href @click.prevent="goToBooking(booking.id, true)">{{ index + 1 }}</a></td>
                    <td><a href v-if="state.waitingListChildNames[booking.childId]"
                           @click.prevent="goToChild(booking.childId)">{{
                            state.waitingListChildNames[booking.childId]
                        }}</a></td>
                    <td>
                            <span v-if="state.waitingListParents[booking.childId]">
                                <a href @click.prevent="goToParent(state.waitingListParents[booking.childId].id)">{{
                                        state.waitingListParents[booking.childId].firstName
                                    }} {{ state.waitingListParents[booking.childId].lastName }}</a>
                                <br>{{ state.waitingListParents[booking.childId].email }}
                                <br>{{ state.waitingListParents[booking.childId].telephone }}
                            </span>
                    </td>
                    <td>{{ formatCurrency(booking.agreedPrice) }}</td>
                    <td>
                        <button class="btn btn-light btn-sm" @click="sendWaitingListBookingReminder(booking.id)">Send
                        </button>
                        <small v-if="booking.bookingNotificationLastSent">{{
                                getDate(booking.bookingNotificationLastSent * 1000)
                            }}</small>
                    </td>
                    <td>{{ state.voucherCodeProviders[booking.franchiseVoucherCodeId] }}</td>
                    <td class="text-end"><input class="btn btn-light btn-sm" type="button" value="Add to bookings"
                                                @click="addToBookings($event, booking.id)"></td>
                </tr>
                </tbody>
            </table>
        </div>

        <div>
            <h2>New course dates email</h2>
            <button :disabled="isButtonDisabled(false) && isButtonDisabled(true)" class="btn btn-light"
                    @click="sendNewCourseDatesEmail">Send
            </button>
            <span class="ms-3">
                Last sent: <strong v-if="newCourseDatesEmailLastSent">{{
                    getDate(newCourseDatesEmailLastSent * 1000)
                }}</strong>
                <em v-else>never</em>
            </span>
        </div>
    </div>
</template>